import React from "react";
import Markdown from "markdown-to-jsx";

const elements = [
	"a",
	"abbr",
	"address",
	"area",
	"article",
	"aside",
	"audio",
	"b",
	"base",
	"bdi",
	"bdo",
	"big",
	"blockquote",
	"body",
	"br",
	"button",
	"canvas",
	"caption",
	"cite",
	"code",
	"col",
	"colgroup",
	"data",
	"datalist",
	"dd",
	"del",
	"details",
	"dfn",
	"dialog",
	"div",
	"dl",
	"dt",
	"em",
	"embed",
	"fieldset",
	"figcaption",
	"figure",
	"footer",
	"form",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"head",
	"header",
	"hgroup",
	"hr",
	"html",
	"i",
	"iframe",
	"img",
	"input",
	"ins",
	"kbd",
	"keygen",
	"label",
	"legend",
	"li",
	"link",
	"main",
	"map",
	"mark",
	"marquee",
	"menu",
	"menuitem",
	"meta",
	"meter",
	"nav",
	"noscript",
	"object",
	"ol",
	"optgroup",
	"option",
	"output",
	"p",
	"param",
	"picture",
	"pre",
	"progress",
	"q",
	"rp",
	"rt",
	"ruby",
	"s",
	"samp",
	"script",
	"section",
	"select",
	"small",
	"source",
	"span",
	"strong",
	"style",
	"sub",
	"summary",
	"sup",
	"table",
	"tbody",
	"td",
	"textarea",
	"tfoot",
	"th",
	"thead",
	"time",
	"title",
	"tr",
	"track",
	"u",
	"ul",
	"var",
	"video",
	"wbr",

	// SVG
	"circle",
	"clipPath",
	"defs",
	"ellipse",
	"foreignObject",
	"g",
	"image",
	"line",
	"linearGradient",
	"marker",
	"mask",
	"path",
	"pattern",
	"polygon",
	"polyline",
	"radialGradient",
	"rect",
	"stop",
	"svg",
	"text",
	"tspan",
];

const allowedElements = ["a", "b", "strong", "em", "u", "code", "del"];

const CustomLink = ({ children, ...props }) => (
	<a {...props} target="_blank" rel="noopener noreferrer">
		{children}
	</a>
);

const SignatureQuote = ({ name }) => (
	<span
		style={{
			display: "flex",
			alignItems: "center",
			gap: 6,
			marginBottom: 2,
			color: "currentColor",
			opacity: 0.85,
			fontWeight: 600,
			fontSize: "0.8rem",
		}}
	>
		<span
			style={{
				width: 3,
				minHeight: 14,
				alignSelf: "stretch",
				borderRadius: 3,
				backgroundColor: "currentColor",
			}}
		/>
		{name}
	</span>
);

const MarkdownWrapper = ({ children }) => {
	if (!children) return null;
	if (typeof children !== "string") children = String(children);

	// Detectar assinatura: "> Nome\nMensagem" ou "> Nome Mensagem"
	const sigMatch = children.match(/^>\s?(.+?)\n([\s\S]*)$/);
	if (sigMatch) {
		return (
			<>
				<SignatureQuote name={sigMatch[1]} />
				<MarkdownWrapper>{sigMatch[2]}</MarkdownWrapper>
			</>
		);
	}
	// Fallback: "> Nome Mensagem" (sem quebra de linha, ex: lastMessage truncado)
	const sigMatchInline = children.match(/^>\s?(\S+)\s+([\s\S]+)$/);
	if (sigMatchInline) {
		return (
			<>
				<SignatureQuote name={sigMatchInline[1]} />
				<MarkdownWrapper>{sigMatchInline[2]}</MarkdownWrapper>
			</>
		);
	}

	const boldRegex = /\*(.*?)\*/g;
	const tildaRegex = /~(.*?)~/g;

	const pixMarker = '###PIX_MARKER###';

	if (children.includes('***')) {
		children = children.replace(/\*\*\*/g, pixMarker);
	}

	if (boldRegex.test(children)) {
		children = children.replace(boldRegex, "**$1**");
	}

	if (children.includes(pixMarker)) {
		children = children.replace(new RegExp(pixMarker, 'g'), '***');
	}

	if (tildaRegex.test(children)) {
		children = children.replace(tildaRegex, "~~$1~~");
	}

	const options = React.useMemo(() => {
		const markdownOptions = {
			disableParsingRawHTML: true,
			forceInline: true,
			overrides: {
				a: { component: CustomLink },
			},
		};

		elements.forEach(element => {
			if (!allowedElements.includes(element)) {
				markdownOptions.overrides[element] = el => el.children || null;
			}
		});

		return markdownOptions;
	}, []);

	return <Markdown options={options}>{children}</Markdown>;
};

export default MarkdownWrapper;
